import { FiltroColumnaValores } from '@/components/filtros/FiltroColumnaValores';
import { FiltroPeriodos } from '@/components/filtros/FiltroPeriodos';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePeriodosDisponibles } from '@/hooks/useAreaCosteo';
import { useMaquinariaDetalleTodo } from '@/hooks/useMaquinariaDetalleTodo';
import type { PeriodoMaqSel } from '@/lib/cliente';
import { formatearCRC } from '@adelante/shared';
import type { RegistroUsoMaquinaria } from '@adelante/shared';
import { CaretDown, CaretLeft, CaretRight, CaretUp, CircleNotch, X } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';

const PERIODO_MAQ_VACIO: PeriodoMaqSel = { aniosSel: [], mesesSel: [], diasSel: [] };
const TAMANIO_PAGINA = 50;

/** Normaliza para búsqueda: minúsculas + sin acentos. */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

const ETIQUETA_ORIGEN_USO: Record<string, string> = {
  PARTE_TRABAJO: 'Parte de Trabajo',
  ASSEMBLY: 'Ensamblado',
};

/** Columnas categóricas con filtro por valores (checkboxes estilo Excel). */
type ColumnaCategoria = 'maquina' | 'tipo' | 'obra' | 'origenUso';
/** Todas las columnas ordenables (incluye las categóricas + numéricas/fecha). */
type Columna = ColumnaCategoria | 'fecha' | 'horas' | 'tarifa' | 'monto';
type Direccion = 'asc' | 'desc';

/** Valor mostrado/filtrado de cada columna categórica para una fila. */
function valorCategoria(f: RegistroUsoMaquinaria, col: ColumnaCategoria): string {
  switch (col) {
    case 'maquina':
      return f.nombreMaquina ? `${f.codigoMaquina} — ${f.nombreMaquina}` : f.codigoMaquina;
    case 'tipo':
      return f.tipoMaquina ?? '(—)';
    case 'obra':
      return f.proyectoDisplay ? `${f.worksNo} — ${f.proyectoDisplay}` : f.worksNo;
    case 'origenUso':
      return ETIQUETA_ORIGEN_USO[f.origenUso] ?? f.origenUso;
  }
}

const VALOR_ORDEN: Record<Columna, (f: RegistroUsoMaquinaria) => string | number> = {
  maquina: (f) => valorCategoria(f, 'maquina'),
  tipo: (f) => valorCategoria(f, 'tipo'),
  obra: (f) => valorCategoria(f, 'obra'),
  origenUso: (f) => valorCategoria(f, 'origenUso'),
  fecha: (f) => f.fecha,
  horas: (f) => f.horasUso ?? 0,
  tarifa: (f) => f.tarifaAplicadaLcy,
  monto: (f) => f.montoCostoObraLcy,
};

/**
 * Vista Maquinaria Detallada: lista de registros de uso de maquinaria
 * (fact_uso_maquinaria), una fila por transacción. Trae TODO el dataset que
 * matchea los filtros (sin paginar en el servidor) y pagina/ordena/filtra
 * por columna en el cliente — necesario para que el filtro estilo Excel vea
 * los valores distintos de cada columna en todo el dataset.
 */
export function MaquinariaDetalladaPantalla() {
  const [pagina, setPagina] = useState(1);
  const [busqueda, setBusqueda] = useState('');
  const [periodoMaq, setPeriodoMaq] = useState<PeriodoMaqSel>(PERIODO_MAQ_VACIO);
  const [orden, setOrden] = useState<{ columna: Columna; direccion: Direccion }>({
    columna: 'fecha',
    direccion: 'desc',
  });
  const [filtrosColumna, setFiltrosColumna] = useState<
    Record<ColumnaCategoria, Set<string> | null>
  >({ maquina: null, tipo: null, obra: null, origenUso: null });

  const { data: fechasData } = usePeriodosDisponibles();
  const { data, isLoading, error } = useMaquinariaDetalleTodo(periodoMaq);

  const filas = data?.data?.filas ?? [];
  const busquedaNorm = normalizar(busqueda);

  const valoresPorColumna = useMemo(() => {
    const m: Record<ColumnaCategoria, string[]> = {
      maquina: [],
      tipo: [],
      obra: [],
      origenUso: [],
    };
    const sets: Record<ColumnaCategoria, Set<string>> = {
      maquina: new Set(),
      tipo: new Set(),
      obra: new Set(),
      origenUso: new Set(),
    };
    for (const f of filas) {
      sets.maquina.add(valorCategoria(f, 'maquina'));
      sets.tipo.add(valorCategoria(f, 'tipo'));
      sets.obra.add(valorCategoria(f, 'obra'));
      sets.origenUso.add(valorCategoria(f, 'origenUso'));
    }
    for (const col of Object.keys(sets) as ColumnaCategoria[]) {
      m[col] = [...sets[col]].sort((a, b) => a.localeCompare(b, 'es', { numeric: true }));
    }
    return m;
  }, [filas]);

  const filasFiltradas = useMemo(() => {
    let resultado = filas;
    for (const col of ['maquina', 'tipo', 'obra', 'origenUso'] as ColumnaCategoria[]) {
      const sel = filtrosColumna[col];
      if (sel && sel.size < valoresPorColumna[col].length) {
        resultado = resultado.filter((f) => sel.has(valorCategoria(f, col)));
      }
    }
    if (busquedaNorm) {
      resultado = resultado.filter(
        (f) =>
          normalizar(f.codigoMaquina).includes(busquedaNorm) ||
          normalizar(f.nombreMaquina ?? '').includes(busquedaNorm) ||
          normalizar(f.worksNo).includes(busquedaNorm),
      );
    }
    return resultado;
  }, [filas, filtrosColumna, valoresPorColumna, busquedaNorm]);

  const filasOrdenadas = useMemo(() => {
    const { columna, direccion } = orden;
    const signo = direccion === 'asc' ? 1 : -1;
    const getValor = VALOR_ORDEN[columna];
    return [...filasFiltradas].sort((a, b) => {
      const va = getValor(a);
      const vb = getValor(b);
      if (typeof va === 'string' || typeof vb === 'string') {
        return signo * String(va).localeCompare(String(vb), 'es', { numeric: true });
      }
      return signo * ((va as number) - (vb as number));
    });
  }, [filasFiltradas, orden]);

  // Si el filtro/orden/búsqueda deja menos páginas de las que había, volver a página 1.
  const totalPaginasCliente = Math.max(1, Math.ceil(filasOrdenadas.length / TAMANIO_PAGINA));
  useEffect(() => {
    if (pagina > totalPaginasCliente) setPagina(1);
  }, [pagina, totalPaginasCliente]);

  const filasPagina = filasOrdenadas.slice((pagina - 1) * TAMANIO_PAGINA, pagina * TAMANIO_PAGINA);

  const cambiarPeriodo = (sel: PeriodoMaqSel) => {
    setPeriodoMaq(sel);
    setPagina(1);
  };

  const ordenarPor = (columna: Columna) => {
    setOrden((p) =>
      p.columna === columna
        ? { columna, direccion: p.direccion === 'asc' ? 'desc' : 'asc' }
        : { columna, direccion: 'asc' },
    );
    setPagina(1);
  };

  const filtrarColumna = (col: ColumnaCategoria, seleccion: Set<string> | null) => {
    setFiltrosColumna((p) => ({ ...p, [col]: seleccion }));
    setPagina(1);
  };

  const IconoOrden = ({ columna }: { columna: Columna }) => {
    if (orden.columna !== columna) return null;
    return orden.direccion === 'asc' ? (
      <CaretUp className="h-3 w-3 inline" weight="bold" />
    ) : (
      <CaretDown className="h-3 w-3 inline" weight="bold" />
    );
  };

  const encabezado = (
    columna: Columna,
    etiqueta: string,
    opciones: { alinear?: 'right'; filtro?: ColumnaCategoria } = {},
  ) => (
    <TableHead
      className={`text-xs select-none ${opciones.alinear === 'right' ? 'text-right' : ''}`}
    >
      <span className="inline-flex items-center gap-1">
        <button
          type="button"
          onClick={() => ordenarPor(columna)}
          className="inline-flex items-center gap-1 hover:text-foreground cursor-pointer"
          title="Ordenar"
        >
          {etiqueta}
          <IconoOrden columna={columna} />
        </button>
        {opciones.filtro && (
          <FiltroColumnaValores
            etiqueta={etiqueta}
            valores={valoresPorColumna[opciones.filtro]}
            seleccion={filtrosColumna[opciones.filtro]}
            onChange={(sel) => filtrarColumna(opciones.filtro!, sel)}
          />
        )}
      </span>
    </TableHead>
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <CircleNotch className="h-4 w-4 animate-spin" weight="bold" />
        Cargando…
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error al cargar: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Maquinaria Detallada</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Lista de registros de uso de maquinaria (partes de trabajo y consumo vía ensamblados), una
          fila por transacción. La tarifa aplicada es ingreso para la máquina y costo para la obra
          donde se utilizó. Click en un encabezado para ordenar; el ícono de embudo filtra por
          valores de esa columna.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Período (solo Maquinaria):</span>
        <FiltroPeriodos
          fechasDisponibles={fechasData?.data ?? []}
          aniosSel={periodoMaq.aniosSel}
          mesesSel={periodoMaq.mesesSel}
          diasSel={periodoMaq.diasSel}
          onChange={cambiarPeriodo}
        />
        <span className="text-xs text-muted-foreground">
          — sin selección, muestra el histórico completo.
        </span>
      </div>

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-medium">
            Registros ({filasOrdenadas.length.toLocaleString('es-CR')} de{' '}
            {filas.length.toLocaleString('es-CR')})
          </CardTitle>
          <div className="relative">
            <input
              type="text"
              value={busqueda}
              onChange={(e) => {
                setBusqueda(e.target.value);
                setPagina(1);
              }}
              placeholder="Buscar máquina u obra…"
              className="h-7 w-56 rounded border border-zinc-300 pl-2 pr-6 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
            {busqueda && (
              <button
                type="button"
                onClick={() => setBusqueda('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-zinc-100 hover:text-foreground"
                title="Limpiar búsqueda"
                aria-label="Limpiar búsqueda"
              >
                <X className="h-3 w-3" weight="bold" />
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {encabezado('fecha', 'Fecha')}
                {encabezado('maquina', 'Máquina', { filtro: 'maquina' })}
                {encabezado('tipo', 'Tipo', { filtro: 'tipo' })}
                {encabezado('obra', 'Obra', { filtro: 'obra' })}
                {encabezado('origenUso', 'Origen Uso', { filtro: 'origenUso' })}
                {encabezado('horas', 'Horas', { alinear: 'right' })}
                {encabezado('tarifa', 'Tarifa', { alinear: 'right' })}
                {encabezado('monto', 'Monto', { alinear: 'right' })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filasPagina.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-xs text-muted-foreground py-6">
                    {busqueda || Object.values(filtrosColumna).some((s) => s)
                      ? 'Sin resultados para el filtro aplicado.'
                      : 'Sin registros.'}
                  </TableCell>
                </TableRow>
              ) : (
                filasPagina.map((f) => (
                  <TableRow key={`${f.origenUso}-${f.entryNo}-${f.documentNo}`}>
                    <TableCell className="text-xs">{f.fecha}</TableCell>
                    <TableCell className="text-xs">
                      {f.codigoMaquina}
                      {f.nombreMaquina && (
                        <span className="text-muted-foreground"> — {f.nombreMaquina}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{f.tipoMaquina ?? '(—)'}</TableCell>
                    <TableCell className="text-xs">
                      {f.worksNo}
                      {f.proyectoDisplay && (
                        <span className="text-muted-foreground"> — {f.proyectoDisplay}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {ETIQUETA_ORIGEN_USO[f.origenUso] ?? f.origenUso}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">
                      {f.horasUso != null ? f.horasUso.toLocaleString('es-CR') : '—'}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">
                      {formatearCRC(f.tarifaAplicadaLcy)}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">
                      {formatearCRC(f.montoCostoObraLcy)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          Página {pagina} de {totalPaginasCliente}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPagina((p) => Math.max(1, p - 1))}
            disabled={pagina <= 1}
            className="flex items-center gap-1 rounded border border-zinc-300 px-2 py-1 disabled:opacity-40"
          >
            <CaretLeft className="h-3 w-3" weight="bold" />
            Anterior
          </button>
          <button
            type="button"
            onClick={() => setPagina((p) => Math.min(totalPaginasCliente, p + 1))}
            disabled={pagina >= totalPaginasCliente}
            className="flex items-center gap-1 rounded border border-zinc-300 px-2 py-1 disabled:opacity-40"
          >
            Siguiente
            <CaretRight className="h-3 w-3" weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
